import React, { useState, useEffect } from 'react';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { Flex, Typography, Box, SelectDropdown, Card, IconButton } from '@kistech/kis-ui';
import { ConfigMappingProps, MappingRelation } from '../../types';
import { useStyles, Tooltips } from '../../styled';
import { MappingEditDrawer } from '../MappingEditDrawer';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Typography as MuiTypography
} from '@mui/material';
import { getFormattedValue } from '../../utils';
import { ActionOverlay } from 'components/ActionOverlay';
import { selectors as kioskSelectors, Kiosk } from 'store/kiosks';
import { Hardware } from 'store/hardware';
import {
  actions as kioskHardwareActions,
  selectors as kioskHardwareSelectors,
  KioskHardware
} from 'store/kioskHardware';
import {
  actions as kioskTicketTemplateActions,
  selectors as kioskTicketTemplateSelectors,
  KioskTicketTemplate
} from 'store/kioskTicketTemplates';
import { ConfigCreateOverlay } from '../../components/ConfigCreateOverlay';
import { getSortedList } from 'utils/stringFormat';

export const ConfigMapping = (props: ConfigMappingProps) => {
  const { mappingData, mappingType, columns, onDeleteConfirm } = props;
  const kiosks = useSelector(kioskSelectors.selectKiosks);
  const kioskHardware = useSelector(kioskHardwareSelectors.selectKioskHardware);
  const kioskTicketTemplates = useSelector(kioskTicketTemplateSelectors.selectKioskTicketTemplates);
  const [selectedKiosk, setSelectedKiosk] = useState<Kiosk | null>(null);
  const [selectedRelation, setSelectedRelation] = useState<MappingRelation | null>(null);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const classes = useStyles({ showDrawer })();

  const [getKioskHardware, resetKioskHardware, resetKioskHardwareAction] = useActions([
    kioskHardwareActions.getKioskHardware,
    kioskHardwareActions.resetKioskHardware,
    kioskHardwareActions.resetAction
  ]);

  const [getKioskTicketTemplates, resetKioskTicketTemplates, resetKioskTicketTemplateAction] = useActions([
    kioskTicketTemplateActions.getKioskTicketTemplates,
    kioskTicketTemplateActions.resetKioskTicketTemplates,
    kioskTicketTemplateActions.resetAction
  ]);

  const { success: kioskHardwareSuccess } = useSelector(kioskHardwareSelectors.selectAction);
  const { success: kioskTicketTemplateSuccess } = useSelector(kioskTicketTemplateSelectors.selectAction);

  useEffect(() => {
    resetKioskHardware();
    resetKioskTicketTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRelation) {
      const updatedKioskMapping = kioskHardware.find((kh) => kh.id === selectedRelation.id);
      if (updatedKioskMapping) {
        setSelectedRelation(updatedKioskMapping);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardware]);

  useEffect(() => {
    if (selectedRelation) {
      const updatedKioskMapping = kioskTicketTemplates.find((ktt) => ktt.id === selectedRelation.id);
      if (updatedKioskMapping) {
        setSelectedRelation(updatedKioskMapping);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskTicketTemplates]);

  useEffect(() => {
    if (kioskHardwareSuccess && selectedKiosk) {
      getKioskHardware({ kioskId: selectedKiosk.id });
      resetKioskHardwareAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwareSuccess]);

  useEffect(() => {
    if (kioskTicketTemplateSuccess && selectedKiosk) {
      getKioskTicketTemplates({ kioskId: selectedKiosk.id });
      resetKioskTicketTemplateAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskTicketTemplateSuccess]);

  const onKioskSelect = (selected: Kiosk) => {
    setSelectedKiosk(selected);
    if (mappingType === 'kioskHardware') {
      resetKioskHardwareAction();
      getKioskHardware({ kioskId: selected.id });
    } else if (mappingType === 'kioskTicketTemplate') {
      resetKioskTicketTemplateAction();
      getKioskTicketTemplates({ kioskId: selected.id });
    }
  };

  const onEditClick = (selected: MappingRelation) => {
    setSelectedRelation(selected);
    setShowDrawer(true);
  };

  const onDeleteConfigClick = (selected: MappingRelation) => {
    setSelectedRelation(selected);
    setShowDeleteOverlay(true);
  };

  const onConfigDeleteConfirm = () => {
    const payload = {
      id: selectedRelation && selectedRelation.id,
      kioskId: selectedRelation && selectedRelation.kioskId
    };
    onDeleteConfirm(mappingType, payload);
    setShowDeleteOverlay(false);
  };

  const resetOverlay = () => {
    setShowDrawer(false);
    setShowDeleteOverlay(false);
  };

  const getName = (mappingRelation: MappingRelation): string => {
    let displayName = '';
    if (mappingType === 'kioskHardware') {
      const relationId = (mappingRelation as KioskHardware).hardwareId;
      const hardware = mappingData.find((h) => h.id === relationId);
      if (hardware) {
        displayName = `${hardware.name} (${hardware.type})`;
      }
    } else if (mappingType === 'kioskTicketTemplate') {
      const relationId = (mappingRelation as KioskTicketTemplate).ticketTemplateId;
      displayName = relationId;
      const ticketTemplate = mappingData.find((tt) => tt.id === relationId);
      if (ticketTemplate) {
        displayName = `${ticketTemplate.id} (${ticketTemplate.type})`;
      }
    }
    return displayName;
  };

  const selectedHardware = () => {
    let item;
    if (mappingType === 'kioskHardware') {
      const relationId = (selectedRelation as KioskHardware).hardwareId;
      item = mappingData.find((hardware) => hardware.id === relationId);
    }
    return item;
  };

  return (
    <>
      <Flex direction="column" flexOverride="1 1 auto" maxWidth="100%">
        <Flex direction="row" margin="0 0 6 0" fullWidth align="center">
          <Typography size={24} weight={700}>
            Kiosk
          </Typography>
          <Box width={64} />
          <SelectDropdown
            items={getSortedList(kiosks, 'name')}
            itemToString={(item: Kiosk) => item && item.name}
            selectedItem={selectedKiosk as Kiosk}
            onChange={(kiosk: Kiosk) => onKioskSelect(kiosk)}
            placeholder="Select a kiosk"
            data-testid="media-selector"
          />
        </Flex>
        {/* TABLE */}
        <MuiBox className={classes.configMappingTableOuterBox}>
          <Card hasBorderRadius raised fullHeight>
            <TableContainer id="userViewTable" className={classes.configTableContainer}>
              <MuiTable stickyHeader style={{ borderCollapse: 'collapse', width: '100%' }}>
                <TableHead>
                  <TableRow className={classes.configMappingTableRows}>
                    {columns &&
                      columns.sort().map((column) => {
                        return (
                          <TableCell
                            className={classes.configMappingHeadersProperty}
                            style={{ width: column.width ? column.width : 'fit-content' }}
                          >
                            {' '}
                            {column.displayName}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {kioskHardware &&
                    kioskHardware.sort().map((kh) => {
                      return (
                        <TableRow className={classes.configMappingTableRows}>
                          <TableCell>
                            <Typography size={16}>{getName(kh)}</Typography>
                          </TableCell>
                          <TableCell className={classes.configMappingTableValueCell}>
                            <Typography size={16}>{getFormattedValue(kh.configuration)}</Typography>
                          </TableCell>
                          <TableCell className={classes.configMappingTableValueCell}>
                            <Typography size={16}>{getFormattedValue(kh.serialCode)}</Typography>
                          </TableCell>
                          <TableCell className={classes.configMappingTableValueCell}>
                            <Typography size={16}>{getFormattedValue(kh.status)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Flex align="stretch" fullWidth>
                              <IconButton
                                name="exit_circle"
                                onClick={() => onDeleteConfigClick(kh)}
                                data-testid="delete-config-button"
                                style={{ marginRight: '15px' }}
                              />
                              <IconButton
                                name="edit_circle"
                                onClick={() => onEditClick(kh)}
                                data-testid="update-user-button"
                              />
                            </Flex>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {kioskTicketTemplates &&
                    kioskTicketTemplates.sort().map((kioskTicketTemplate) => {
                      return (
                        <TableRow className={classes.configMappingTableRows}>
                          <TableCell>
                            <Typography size={16}>{getName(kioskTicketTemplate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Flex align="stretch" fullWidth>
                              <IconButton
                                name="exit_circle"
                                onClick={() => onDeleteConfigClick(kioskTicketTemplate)}
                                data-testid="delete-config-button"
                                style={{ marginRight: '15px' }}
                              />
                            </Flex>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </MuiTable>
            </TableContainer>
            {selectedKiosk && (
              <MuiBox className={classes.addConfigRow}>
                <Tooltips title="Add Config" arrow placement={'top'}>
                  <IconButton name="plus_circle" onClick={() => setShowCreateOverlay(true)} />
                </Tooltips>
              </MuiBox>
            )}
          </Card>
        </MuiBox>
      </Flex>

      {selectedKiosk && showCreateOverlay && (
        <ConfigCreateOverlay
          kiosk={selectedKiosk}
          configType={mappingType}
          onCancel={() => setShowCreateOverlay(false)}
        />
      )}

      {showDrawer && (
        <MappingEditDrawer
          onClose={resetOverlay}
          isOpen={showDrawer}
          configData={selectedRelation as KioskHardware}
          configItem={selectedHardware() as Hardware}
          kiosk={selectedKiosk as Kiosk} // Always there will be a selectedKiosk
        />
      )}

      {showDeleteOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <MuiTypography className={classes.configDeleteHeader} style={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete?
              </MuiTypography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onConfigDeleteConfirm()}
          cancelText="Back"
          onCancel={resetOverlay}
          width={800}
          appearance="error"
        />
      )}
    </>
  );
};
